import { Vector3, type Camera } from "three";
import { bearing } from "../../ui/hud/compass-model.ts";
import { CompassHud, type CompassTarget } from "../../ui/hud/compass-hud.ts";
import { CornerButtons, type CornerButton } from "../../ui/hud/corner-buttons.ts";
import { Minimap } from "../../ui/hud/minimap.ts";
import { h } from "../../ui/kit/dom.ts";
import type { IslandLayers } from "../../ui/panels/island-map.ts";
import type { FogOfWar, MapCircle, MapMarker } from "../../ui/panels/map-model.ts";

/**
 * Navegação do HUD (F81, mockup ui-b; R2: fora da `GameScene`): a bússola no alto e, na coluna da direita,
 * os botões do canto e o minimapa redondo. A coluna (`column`) recebe também o Relicário; os avisos
 * (`#toasts`, por cima dos painéis) ficam logo abaixo dela pelo CSS (`body.hud-partida`). A cena passa os
 * marcadores do mapa a 10 Hz (`setMarkers`, que redesenha o minimapa) e chama `frame` por quadro (a bússola
 * e a seta do minimapa giram com a câmera).
 */
export class NavHud {
  /** Coluna da direita: botões, minimapa e Relicário, de cima para baixo. */
  readonly column: HTMLElement;
  private readonly compass: CompassHud;
  private readonly minimap: Minimap;
  private readonly dir = new Vector3();
  private targets: CompassTarget[] = [];

  constructor(parent: HTMLElement, layers: IslandLayers, buttons: readonly CornerButton[]) {
    this.compass = new CompassHud(parent);
    this.column = h("div", { id: "hud-direita" });
    parent.appendChild(this.column);
    new CornerButtons(this.column, buttons);
    this.minimap = new Minimap(this.column, layers);
    document.body.classList.add("hud-partida");
  }

  /** Marcadores do mapa (≤ 10 Hz): redesenha o minimapa em volta de (cx, cz) e guarda os alvos da bússola. */
  setMarkers(cx: number, cz: number, fog: FogOfWar, markers: readonly MapMarker[], circles: readonly MapCircle[]): void {
    this.minimap.draw(cx, cz, fog, markers, circles);
    this.targets = compassTargets(markers);
  }

  /** Por quadro: o rumo da câmera gira a faixa da bússola, as marcas e a seta do minimapa. */
  frame(camera: Camera): void {
    camera.getWorldDirection(this.dir);
    const heading = bearing(this.dir.x, this.dir.z);
    this.compass.update(heading, camera.position.x, camera.position.z, this.targets);
    this.minimap.turn(heading);
  }

  remove(): void {
    this.compass.remove();
    this.column.remove();
    document.body.classList.remove("hud-partida");
  }
}

/** Da lista do mapa, o que a bússola mostra: aliados (o número), pings (o símbolo) e chefes (a coroa). */
export function compassTargets(markers: readonly MapMarker[]): CompassTarget[] {
  const out: CompassTarget[] = [];
  markers.forEach((m) => {
    if (m.kind !== "aliado" && m.kind !== "ping" && m.kind !== "chefe") return;
    // Id para o DOM: o do marcador (estável; a ordem mudando não refaz as marcas, revisão da F81).
    out.push({ id: m.id, kind: m.kind, x: m.x, z: m.z, glyph: m.symbol, color: m.color });
  });
  return out;
}
